import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Camera, Keyboard, Loader2, AlertCircle } from "lucide-react";

const READER_ID = "jscan-qr-reader";

function extractCode(text) {
  if (!text) return null;
  const clean = text.trim();
  try {
    const u = new URL(clean);
    const m = u.pathname.match(/\/q\/([A-Za-z0-9]+)/);
    if (m) return m[1].toUpperCase();
  } catch (_) {
    /* not a URL */
  }
  const m2 = clean.match(/\/q\/([A-Za-z0-9]+)/);
  if (m2) return m2[1].toUpperCase();
  if (/^[A-Za-z0-9]{6,12}$/.test(clean)) return clean.toUpperCase();
  return null;
}

// Suppress benign unhandled rejections from html5-qrcode when the video is
// interrupted mid-startup (mode switch / dialog close). These are known-benign.
function isBenignScannerError(msg) {
  if (!msg) return false;
  const s = String(msg).toLowerCase();
  return (
    s.includes("play()") ||
    s.includes("the play() request was interrupted") ||
    s.includes("the fetching process for the media resource was aborted") ||
    s.includes("removefromdocument") ||
    s.includes("aborterror")
  );
}

export default function ScanQRDialog({ open, onOpenChange, onResolved }) {
  const [mode, setMode] = useState("manual");
  const [starting, setStarting] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const scannerRef = useRef(null);
  const lastDecodedRef = useRef(null);
  const startingPromiseRef = useRef(null);

  // Global suppressor for html5-qrcode's play() abort noise
  useEffect(() => {
    const handler = (e) => {
      const msg = e?.reason?.message || e?.reason || "";
      if (isBenignScannerError(msg)) {
        e.preventDefault();
      }
    };
    window.addEventListener("unhandledrejection", handler);
    return () => window.removeEventListener("unhandledrejection", handler);
  }, []);

  const stopScanner = async () => {
    // Wait for any in-flight start to settle first
    if (startingPromiseRef.current) {
      try {
        await startingPromiseRef.current;
      } catch (_) { /* ignore */ }
    }
    const scanner = scannerRef.current;
    scannerRef.current = null;
    setRunning(false);
    if (!scanner) return;
    try {
      if (scanner.isScanning) {
        await scanner.stop();
      }
    } catch (_) { /* ignore */ }
    try { scanner.clear(); } catch (_) { /* ignore */ }
  };

  const resolveCode = async (code) => {
    setResolving(true);
    try {
      const { data } = await api.get(`/qrs/by-code/${code}`);
      toast.success(`QR ${data.code} ditemukan`);
      onResolved?.(data);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setResolving(false);
    }
  };

  const handleDecoded = async (text) => {
    if (lastDecodedRef.current === text) return;
    lastDecodedRef.current = text;
    const code = extractCode(text);
    if (!code) {
      toast.error("QR tidak dikenali sebagai QR J-scan");
      setTimeout(() => (lastDecodedRef.current = null), 1500);
      return;
    }
    await stopScanner();
    await resolveCode(code);
  };

  const startScanner = async () => {
    setError(null);
    setStarting(true);
    let scanner;
    try {
      const el = document.getElementById(READER_ID);
      if (!el) throw new Error("Kontainer kamera tidak siap");
      scanner = new Html5Qrcode(READER_ID, { verbose: false });
      scannerRef.current = scanner;
      const config = {
        fps: 10,
        qrbox: (vw, vh) => {
          const min = Math.min(vw, vh);
          const size = Math.max(60, Math.floor(min * 0.75));
          return { width: size, height: size };
        },
        aspectRatio: 1.0,
      };
      const p = scanner.start(
        { facingMode: "environment" },
        config,
        (decodedText) => handleDecoded(decodedText),
        () => { /* per-frame decode error, ignore */ }
      );
      startingPromiseRef.current = p;
      await p;
      // If we were unmounted mid-start, tear down what we started
      if (scannerRef.current !== scanner) {
        try { if (scanner.isScanning) await scanner.stop(); } catch (_) { /* ignore */ }
        try { scanner.clear(); } catch (_) { /* ignore */ }
        return;
      }
      setRunning(true);
    } catch (err) {
      const msg = err?.message || String(err);
      if (!isBenignScannerError(msg)) {
        setError(msg);
        if (msg.toLowerCase().includes("permission") || msg.toLowerCase().includes("notallowed")) {
          toast.error("Izin kamera ditolak. Gunakan input manual.");
        }
      }
      scannerRef.current = null;
    } finally {
      setStarting(false);
      startingPromiseRef.current = null;
    }
  };

  useEffect(() => {
    let cancelled = false;
    if (open && mode === "camera") {
      lastDecodedRef.current = null;
      const t = setTimeout(() => {
        if (!cancelled) startScanner();
      }, 150);
      return () => {
        cancelled = true;
        clearTimeout(t);
        stopScanner();
      };
    }
    if (!open) {
      stopScanner();
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode]);

  const handleManual = async (e) => {
    e.preventDefault();
    const code = extractCode(manualCode);
    if (!code) { toast.error("Kode tidak valid"); return; }
    await resolveCode(code);
    setManualCode("");
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          stopScanner();
          setManualCode("");
          setError(null);
        }
        onOpenChange(o);
      }}
    >
      <DialogContent className="sm:max-w-md" data-testid="scan-qr-dialog">
        <DialogHeader>
          <DialogTitle className="jscan-brand text-2xl">Scan QR</DialogTitle>
          <DialogDescription>
            Arahkan kamera ke QR akrilik. Setelah terbaca, form edit akan terbuka otomatis.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2">
          <Button
            variant={mode === "camera" ? "default" : "outline"}
            size="sm"
            onClick={() => setMode("camera")}
            className="rounded-full"
            data-testid="scan-mode-camera"
          >
            <Camera className="w-4 h-4 mr-2" /> Kamera
          </Button>
          <Button
            variant={mode === "manual" ? "default" : "outline"}
            size="sm"
            onClick={() => setMode("manual")}
            className="rounded-full"
            data-testid="scan-mode-manual"
          >
            <Keyboard className="w-4 h-4 mr-2" /> Ketik kode
          </Button>
        </div>

        {/* Reader is always mounted so html5-qrcode's video element is never
            removed from DOM mid-play. We hide it when in manual mode. */}
        <div className={mode === "camera" ? "space-y-3" : "hidden"}>
          <div
            id={READER_ID}
            className="w-full aspect-square rounded-xl overflow-hidden bg-slate-900 relative"
            data-testid="scan-reader"
          >
            {(starting || (!running && !error)) && (
              <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Membuka kamera...
              </div>
            )}
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-700 dark:text-rose-300">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-medium">Kamera tidak bisa dibuka</div>
                <div className="text-xs opacity-80">
                  {error}. Coba izinkan kamera di browser, atau pakai "Ketik kode".
                </div>
              </div>
            </div>
          )}

          <p className="text-xs text-muted-foreground text-center">
            Kamera hanya aktif di halaman ini. Pastikan pencahayaan cukup.
          </p>
        </div>

        {mode === "manual" && (
          <form onSubmit={handleManual} className="space-y-3">
            <div>
              <label className="text-sm font-medium">Kode QR</label>
              <Input
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                placeholder="mis. 5WH27Z3M atau URL /q/..."
                className="jscan-mono mt-2"
                autoFocus
                data-testid="scan-manual-input"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Salin dari stiker atau tempel URL lengkapnya.
              </p>
            </div>
            <Button
              type="submit"
              disabled={resolving || !manualCode.trim()}
              className="w-full rounded-full bg-sky-600 hover:bg-sky-700"
              data-testid="scan-manual-submit"
            >
              {resolving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Cari QR
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
